import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Chip,
  Divider,
  FormControlLabel,
  Checkbox,
  FormGroup,
} from "@mui/material";
import { User } from "../../pages/playground/types";
import { AuthorizationJob } from "./authorization-types";
import { setBulkJobAuthorizations } from "../../api/authorization";

interface JobAssignmentDialogProps {
  open: boolean;
  onClose: () => void;
  copywriters: User[];
  selectedJobs: AuthorizationJob[];
  assignedJobs: AuthorizationJob[];
  onAssignmentComplete: () => void;
}

export const JobAssignmentDialog: React.FC<JobAssignmentDialogProps> = ({
  open,
  onClose,
  copywriters,
  selectedJobs,
  assignedJobs,
  onAssignmentComplete,
}) => {
  const [selectedCopywriter, setSelectedCopywriter] = useState<string>("");
  const [selectedRoles, setSelectedRoles] = useState<("Editor" | "Reviewer")[]>(
    []
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedCopywriter("");
      setSelectedRoles([]);
      setError(null);
    }
  }, [open]);

  // Handle assignment submission
  const handleAssign = async () => {
    if (!selectedCopywriter) {
      setError("Select a copywriter to continue");
      return;
    }

    if (selectedRoles.length === 0) {
      setError("Select at least one role");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Get IDs of selected jobs
      const jobIds: string[] = selectedJobs.map((job) => job.id);

      assignedJobs.forEach((job) => {
        const assignments = job.assignments;

        assignments.forEach((assignment) => {
          if (
            assignment.user_id === selectedCopywriter &&
            selectedRoles.includes(
              assignment.function as "Editor" | "Reviewer"
            ) &&
            !jobIds.includes(job.id)
          ) {
            jobIds.push(job.id);
          }
        });
      });

      // Call the API to assign the jobs
      await setBulkJobAuthorizations(selectedCopywriter, jobIds, selectedRoles);

      // Notify parent component to refresh data
      onAssignmentComplete();

      // Close the dialog
      onClose();
    } catch (error) {
      console.error("Error assigning jobs:", error);
      setError("An error occurred during job assignment");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Assign {selectedJobs.length}{" "}
        {selectedJobs.length === 1 ? "job" : "jobs"}
      </DialogTitle>

      <DialogContent>
        {/* Selected jobs summary */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Selected jobs:
          </Typography>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, my: 1 }}>
            {selectedJobs.map((job) => (
              <Chip
                key={job.id}
                label={job.sku}
                size="small"
                variant="outlined"
              />
            ))}
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Copywriter selection */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Select copywriter:
          </Typography>

          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel id="copywriter-select-label">Copywriter</InputLabel>
            <Select
              labelId="copywriter-select-label"
              value={selectedCopywriter}
              onChange={(e) => setSelectedCopywriter(e.target.value)}
              label="Copywriter"
            >
              {copywriters.map((copywriter) => (
                <MenuItem key={copywriter.id} value={copywriter.id}>
                  {copywriter.name} {copywriter.surname} ({copywriter.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Role selection */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Roles (select one or both):
          </Typography>

          <FormGroup sx={{ ml: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedRoles.includes("Editor")}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedRoles((prev) => [...prev, "Editor"]);
                    } else {
                      setSelectedRoles((prev) =>
                        prev.filter((role) => role !== "Editor")
                      );
                    }
                  }}
                />
              }
              label="Copywriter"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedRoles.includes("Reviewer")}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedRoles((prev) => [...prev, "Reviewer"]);
                    } else {
                      setSelectedRoles((prev) =>
                        prev.filter((role) => role !== "Reviewer")
                      );
                    }
                  }}
                />
              }
              label="Reviewer"
            />
          </FormGroup>
        </Box>

        {/* Error message */}
        {error && (
          <Typography color="error" variant="body2" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleAssign}
          variant="contained"
          color="primary"
          disabled={
            isSubmitting || !selectedCopywriter || selectedRoles.length === 0
          }
        >
          {isSubmitting ? "Assignment in progress..." : "Assign"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
